import { clamp } from 'lodash-es';

import { format, units } from '@dynatrace-sdk/units';

interface ProgressAriaValueReturnProps {
  /**
   * The properly clamped progress value or undefined if progress is indeterminate.
   */
  value: number;
  /**
   * indicates if the progress is indeterminate or has a value.
   */
  isIndeterminate: false;
}
interface ProgressAriaIndeterminateReturnProps {
  /**
   * The properly clamped progress value or undefined if progress is indeterminate.
   */
  value: undefined;
  /**
   * indicates if the progress is indeterminate or has a value.
   */
  isIndeterminate: true;
}

type ProgressAriaReturnProps = (
  | ProgressAriaValueReturnProps
  | ProgressAriaIndeterminateReturnProps
) & {
  /**
   * Aria props for a ProgressBar element that is not a native <progress /> element.
   */
  progressAriaProps: {
    'aria-valuenow'?: number;
    'aria-valuetext'?: string;
    'aria-valuemin'?: number;
    'aria-valuemax'?: number;
    role: string;
  };
};

/**
 * Returns aria-props for a progress as well as the properly clamped value property.
 * @internal
 */
export function useProgressAriaProps(
  originalValue: undefined | number | 'indeterminate',
  min: number,
  max: number,
  valueText?: string,
): ProgressAriaReturnProps {
  let value: number | undefined;
  let isIndeterminate: boolean;

  if (originalValue === 'indeterminate' || originalValue === undefined) {
    value = undefined;
    isIndeterminate = true;
  } else {
    value = originalValue;
    isIndeterminate = false;
  }

  if (!isIndeterminate) {
    value = clamp(originalValue as number, min, max);
  }

  if (!isIndeterminate && value !== undefined && !valueText) {
    valueText = format(value, { input: units.percentage.percent });
  }

  const progressAriaProps = {
    // aria-valuenow should only be provided and updated if the value is not indeterminate https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/progressbar_role)
    'aria-valuenow': isIndeterminate ? undefined : value,
    'aria-valuetext': isIndeterminate ? undefined : valueText,
    // aria-valuemin should only be provided if min is not 0 (https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/progressbar_role)
    'aria-valuemin': min !== 0 ? min : undefined,
    // aria-valuemax should only be provided if min is not 100 (https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/progressbar_role)
    'aria-valuemax': max !== 100 ? max : undefined,
    role: 'progressbar',
  };

  if (isIndeterminate === true && value === undefined) {
    return {
      value: undefined,
      isIndeterminate: true,
      progressAriaProps,
    };
  }

  return {
    value: value as number,
    isIndeterminate: false,
    progressAriaProps,
  };
}
